import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import GroupIcon from "@mui/icons-material/Group";
import PeopleIcon from "@mui/icons-material/People";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import TableRowsIcon from "@mui/icons-material/TableRows";
import UserGroupsTable from "../components/UserGroupsTable";

type UserGroupSummary = {
  id: string;
  name: string;
  description?: string;
  color: string;
  platformIds: string[];
};

type PlatformOption = {
  id: string;
  name: string;
};

type UserMembershipSummary = {
  id: string;
  userGroups: string[];
};

type UserMembershipResponse = {
  id: string;
  userGroups?: string[];
};

type UserGroupFormValues = {
  name: string;
  description: string;
  color: string;
  platformIds: string[];
};

const DEFAULT_USER_GROUP_COLOR = "#2563eb";
const USER_GROUPS_VIEW_MODE_STORAGE_KEY = "userGroupsPageViewMode";

type UserGroupsViewMode = "Cards" | "Table";

const defaultValues: UserGroupFormValues = {
  name: "",
  description: "",
  color: DEFAULT_USER_GROUP_COLOR,
  platformIds: []
};

const UserGroupsPage = () => {
  const { authorizedFetch } = useAuth();
  const queryClient = useQueryClient();
  const [userGroupsViewMode, setUserGroupsViewMode] = useState<UserGroupsViewMode>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(USER_GROUPS_VIEW_MODE_STORAGE_KEY);
      if (stored === "Cards" || stored === "Table") {
        return stored;
      }
    }
    return "Cards";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(USER_GROUPS_VIEW_MODE_STORAGE_KEY, userGroupsViewMode);
    } catch (err) {
      console.error("Failed to persist user groups view mode", err);
    }
  }, [userGroupsViewMode]);

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<UserGroupSummary[]>({
    queryKey: ["usergroups"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/usergroups");
      type UserGroupSummaryResponse = Omit<UserGroupSummary, "color" | "platformIds"> & {
        color?: string;
        platformIds?: string[];
      };
      const payload = (await response.json().catch(() => null)) as { userGroups?: UserGroupSummaryResponse[]; error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load user groups");
      }
      const groups = payload?.userGroups ?? [];
      return groups.map((group) => ({
        ...group,
        color: (group.color ?? DEFAULT_USER_GROUP_COLOR).toLowerCase(),
        platformIds: group.platformIds ?? []
      }));
    }
  });

  const userGroups = useMemo(() => data ?? [], [data]);

  const { data: userMemberships } = useQuery<UserMembershipSummary[]>({
    queryKey: ["users", "memberships"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/users");
      const payload = (await response.json().catch(() => null)) as {
        users?: UserMembershipResponse[];
        error?: string;
      } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load users");
      }
      return (payload?.users ?? []).map((user) => ({
        id: user.id,
        userGroups: user.userGroups ?? []
      }));
    }
  });

  const userCountsByGroup = useMemo(() => {
    const counts = new Map<string, number>();
    for (const user of userMemberships ?? []) {
      for (const groupId of user.userGroups) {
        counts.set(groupId, (counts.get(groupId) ?? 0) + 1);
      }
    }
    return counts;
  }, [userMemberships]);

  const {
    data: platformsData,
    isLoading: isLoadingPlatforms,
    isError: isPlatformsError,
    error: platformsError
  } = useQuery<PlatformOption[]>({
    queryKey: ["platforms", "options"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/platforms");
      type PlatformSummaryResponse = { id: string; name: string };
      const payload = (await response.json().catch(() => null)) as { platforms?: PlatformSummaryResponse[]; error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load platforms");
      }
      const platforms = payload?.platforms ?? [];
      return platforms.map((platform) => ({
        id: platform.id,
        name: platform.name
      }));
    }
  });

  const platformOptions = useMemo(() => platformsData ?? [], [platformsData]);
  const platformNameById = useMemo(() => {
    const entries = platformOptions.map((platform) => [platform.id, platform.name] as const);
    return new Map(entries);
  }, [platformOptions]);

  const summarizePlatforms = useCallback(
    (ids: string[]) => {
      if (!ids || ids.length === 0) {
        return "All platforms";
      }
      return ids
        .map((id) => platformNameById.get(id) ?? id)
        .join(", ");
    },
    [platformNameById]
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroupSummary | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteGroup, setDeleteGroup] = useState<UserGroupSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(null);
  const [selectedUserGroupIds, setSelectedUserGroupIds] = useState<string[]>([]);

  const userGroupById = useMemo(() => {
    const entries = userGroups.map((group) => [group.id, group] as const);
    return new Map(entries);
  }, [userGroups]);

  useEffect(() => {
    setSelectedUserGroupIds((previous) => previous.filter((id) => userGroupById.has(id)));
  }, [userGroupById]);

  const validationSchema = useMemo(() => {
    const validPlatformIds = new Set(platformOptions.map((platform) => platform.id));
    return Yup.object({
      name: Yup.string()
        .transform((value) => value?.trim() ?? "")
        .required("Name is required")
        .test("unique-name", "A group with this name already exists", function (value) {
          if (!value) {
            return true;
          }
          const normalized = value.trim().toLowerCase();
          const currentId = editingGroup?.id ?? null;
          return !userGroups.some((group) => group.id !== currentId && group.name.trim().toLowerCase() === normalized);
        }),
      description: Yup.string().transform((value) => value?.trim() ?? ""),
      color: Yup.string()
        .transform((value) => value?.trim() ?? "")
        .required("Color is required")
        .matches(/^#[0-9a-f]{6}$/i, "Color must be a valid hex code"),
      platformIds: Yup.array()
        .of(
          Yup.string()
            .transform((value) => value?.trim() ?? "")
            .min(1, "Invalid platform selection")
        )
        .test("unique-platforms", "Duplicate platform selected", (selected) => {
          if (!selected) {
            return true;
          }
          const seen = new Set<string>();
          for (const id of selected) {
            if (seen.has(id)) {
              return false;
            }
            seen.add(id);
          }
          return true;
        })
        .test("known-platforms", "Unknown platform selected", (selected) => {
          if (!selected || selected.length === 0 || validPlatformIds.size === 0) {
            return true;
          }
          return selected.every((id) => validPlatformIds.has(id));
        })
    });
  }, [editingGroup?.id, platformOptions, userGroups]);

  const invalidateUserGroups = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["usergroups"] });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (values: UserGroupFormValues) => {
      const response = await authorizedFetch("/api/usergroups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to create user group";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidateUserGroups();
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UserGroupFormValues }) => {
      const response = await authorizedFetch(`/api/usergroups/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values)
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to update user group";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidateUserGroups();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await authorizedFetch(`/api/usergroups/${id}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to delete user group";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidateUserGroups();
    }
  });

  const handleOpenCreate = () => {
    setEditingGroup(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (group: UserGroupSummary) => {
    setEditingGroup(group);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingGroup(null);
    setDialogError(null);
  };

  const handleOpenDelete = (group: UserGroupSummary) => {
    setDeleteGroup(group);
    setDeleteError(null);
  };

  const handleCloseDelete = () => {
    setDeleteGroup(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = () => {
    if (!deleteGroup) return;
    setDeleteError(null);
    deleteMutation.mutate(deleteGroup.id, {
      onSuccess: () => {
        setSnackbar({ open: true, message: `${deleteGroup.name} was removed.`, severity: "success" });
        setSelectedUserGroupIds((previous) => previous.filter((id) => id !== deleteGroup.id));
        handleCloseDelete();
      },
      onError: (err) => {
        console.error(err);
        const message = err instanceof Error ? err.message : "Unable to delete user group.";
        setDeleteError(message);
      }
    });
  };

  const errorMessage = isError ? (error instanceof Error ? error.message : "Unable to load user groups") : null;
  const selectedGroup = selectedUserGroupIds.length === 1 ? userGroupById.get(selectedUserGroupIds[0]) : null;

  const handleViewModeChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: UserGroupsViewMode | null) => {
      if (!value) {
        return;
      }
      if (value !== "Table") {
        setSelectedUserGroupIds([]);
      }
      setUserGroupsViewMode(value);
    },
    []
  );

  const viewToggleButtons = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={userGroupsViewMode}
      onChange={handleViewModeChange}
      aria-label="User groups view style"
      color="primary"
      sx={{ ml: { xs: 0, sm: 0.75 } }}
    >
      <ToggleButton value="Cards" aria-label="Cards view">
        <Tooltip title="Cards" describeChild>
          <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <DashboardCustomizeIcon fontSize="small" />
          </Box>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="Table" aria-label="Table view">
        <Tooltip title="Table" describeChild>
          <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TableRowsIcon fontSize="small" />
          </Box>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const actionButtons = (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
      <Button variant="outlined" onClick={handleOpenCreate} sx={{ whiteSpace: "nowrap" }}>
        Add group
      </Button>
      {userGroupsViewMode === "Table" ? (
        <>
          <Tooltip title="Edit group">
            <span>
              <IconButton
                color="primary"
                aria-label="Edit group"
                onClick={() => {
                  if (selectedGroup) {
                    handleOpenEdit(selectedGroup);
                  }
                }}
                disabled={!selectedGroup}
                size="small"
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete group">
            <span>
              <IconButton
                color="primary"
                aria-label="Delete group"
                onClick={() => {
                  if (selectedGroup) {
                    handleOpenDelete(selectedGroup);
                  }
                }}
                disabled={!selectedGroup}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ) : null}
    </Stack>
  );

  const tableToolbarActions = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="flex-end"
      sx={{ width: { xs: "100%", sm: "auto" } }}
    >
      {viewToggleButtons}
      {actionButtons}
    </Stack>
  );

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Box display="inline-flex" alignItems="center" gap={1}>
            <GroupIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h5" sx={{ m: 0 }}>
              User groups
            </Typography>
            <Badge badgeContent={userGroups.length} color="primary" sx={{ ml: 1 }} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Organize users into logical groups for easier administration.
          </Typography>
        </Box>
        {userGroupsViewMode === "Table" && userGroups.length > 0 ? null : (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {viewToggleButtons}
            {actionButtons}
          </Stack>
        )}
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : userGroups.length === 0 ? (
        <Alert severity="info">No user groups defined yet. Create your first group to get started.</Alert>
      ) : userGroupsViewMode === "Cards" ? (
        <Stack spacing={2}>
          {userGroups.map((group) => {
            const memberCount = userCountsByGroup.get(group.id) ?? 0;
            const memberLabel = `${memberCount} ${memberCount === 1 ? "user" : "users"}`;
            return (
              <Card key={group.id} variant="outlined">
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2
                  }}
                >
                  <Box>
                    <Typography variant="h6">{group.name}</Typography>
                    {group.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {group.description}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: group.description ? 1 : 0.5 }}
                    >
                      Platforms: {summarizePlatforms(group.platformIds)}
                    </Typography>
                  </Box>
                  <Tooltip title="Users in the usergroup">
                    <Badge
                      badgeContent={memberCount}
                      color="primary"
                      showZero
                      sx={{ flexShrink: 0 }}
                      aria-label={memberLabel}
                    >
                      <PeopleIcon
                        aria-hidden="true"
                        fontSize="large"
                        sx={{
                          color: group.color || DEFAULT_USER_GROUP_COLOR
                        }}
                      />
                    </Badge>
                  </Tooltip>
                </CardContent>
                <CardActions>
                  <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenEdit(group)}>
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleOpenDelete(group)}>
                    Delete
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <UserGroupsTable
          userGroups={userGroups}
          userCountsByGroup={userCountsByGroup}
          summarizePlatforms={summarizePlatforms}
          selectedUserGroupIds={selectedUserGroupIds}
          onSelectionChange={setSelectedUserGroupIds}
          toolbarActions={userGroupsViewMode === "Table" ? tableToolbarActions : undefined}
        />
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingGroup ? "Edit user group" : "Add user group"}</DialogTitle>
        <Formik
          validateOnMount
          enableReinitialize
          initialValues={
            editingGroup
              ? {
                name: editingGroup.name,
                description: editingGroup.description ?? "",
                color: editingGroup.color?.trim() || DEFAULT_USER_GROUP_COLOR,
                platformIds: [...(editingGroup.platformIds ?? [])]
              }
              : { ...defaultValues }
          }
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setDialogError(null);
            const normalizedColor = values.color.trim() || DEFAULT_USER_GROUP_COLOR;
            const normalizedPlatformIds = Array.from(
              new Set(values.platformIds.map((id) => id.trim()).filter((id) => id.length > 0))
            );
            const payload: UserGroupFormValues = {
              name: values.name.trim(),
              description: values.description.trim(),
              color: normalizedColor.toLowerCase(),
              platformIds: normalizedPlatformIds
            };

            if (editingGroup) {
              updateMutation.mutate(
                { id: editingGroup.id, values: payload },
                {
                  onSuccess: () => {
                    setSnackbar({ open: true, message: `${editingGroup.name} updated.`, severity: "success" });
                    handleCloseDialog();
                  },
                  onError: (err) => {
                    console.error(err);
                    const message = err instanceof Error ? err.message : "Unable to update user group.";
                    setDialogError(message);
                  },
                  onSettled: () => {
                    setSubmitting(false);
                  }
                }
              );
            } else {
              createMutation.mutate(payload, {
                onSuccess: () => {
                  setSnackbar({ open: true, message: `${payload.name} created.`, severity: "success" });
                  handleCloseDialog();
                },
                onError: (err) => {
                  console.error(err);
                  const message = err instanceof Error ? err.message : "Unable to create user group.";
                  setDialogError(message);
                },
                onSettled: () => {
                  setSubmitting(false);
                }
              });
            }
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, isValid, dirty }) => {
            const platformFieldError =
              touched.platformIds && errors.platformIds
                ? Array.isArray(errors.platformIds)
                  ? errors.platformIds.join(", ")
                  : errors.platformIds
                : null;
            const helperMessage = (() => {
              if (platformFieldError) {
                return platformFieldError;
              }
              if (isLoadingPlatforms) {
                return "Loading platforms…";
              }
              if (isPlatformsError) {
                if (platformsError instanceof Error) {
                  return platformsError.message;
                }
                if (typeof platformsError === "string") {
                  return platformsError;
                }
                return "Unable to load platforms.";
              }
              return values.platformIds.length === 0
                ? "All platforms will be accessible."
                : "Choose one or more platforms to limit access.";
            })();
            return (
              <Form>
                <DialogContent>
                  {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
                  <Stack spacing={2}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                      <TextField
                        name="name"
                        label="Name"
                        size="small"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        autoFocus
                        fullWidth
                        sx={{
                          width: { xs: "100%", sm: "80%" },
                          flexBasis: { sm: "80%" },
                          flexGrow: 1
                        }}
                      />
                      <TextField
                        name="color"
                        label="Color"
                        type="color"
                        size="small"
                        value={values.color}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          width: { xs: "100%", sm: "10%" },
                          flexBasis: { sm: "10%" },
                          flexShrink: 0,
                          "& .MuiInputBase-input": {
                            padding: 0,
                            height: 40,
                            minWidth: 40,
                            cursor: "pointer"
                          }
                        }}
                      />
                    </Stack>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(platformFieldError)}
                      disabled={isLoadingPlatforms && platformOptions.length === 0}
                    >
                      <InputLabel id="platformIds-label" shrink>
                        Platforms
                      </InputLabel>
                      <Select
                        multiple
                        name="platformIds"
                        labelId="platformIds-label"
                        label="Platforms"
                        value={values.platformIds}
                        onBlur={handleBlur}
                        onChange={(event: SelectChangeEvent<string[]>) => {
                          const { value } = event.target;
                          const rawValues = typeof value === "string" ? value.split(",") : value;
                          const cleaned = Array.from(
                            new Set(
                              (rawValues ?? [])
                                .map((id) => (id ?? "").toString().trim())
                                .filter((id) => id.length > 0)
                            )
                          );
                          setFieldValue("platformIds", cleaned);
                        }}
                        renderValue={(selected) => {
                          const selections = (Array.isArray(selected) ? selected : [selected]).map((value) =>
                            String(value)
                          );
                          if (selections.length === 0 || selections[0] === "") {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                All platforms
                              </Typography>
                            );
                          }
                          return summarizePlatforms(selections);
                        }}
                        displayEmpty
                      >
                        {isLoadingPlatforms ? (
                          <MenuItem disabled value="__loading">
                            Loading platforms…
                          </MenuItem>
                        ) : isPlatformsError ? (
                          <MenuItem disabled value="__error">
                            Unable to load platforms
                          </MenuItem>
                        ) : platformOptions.length === 0 ? (
                          <MenuItem disabled value="__empty">
                            No platforms available
                          </MenuItem>
                        ) : (
                          platformOptions.map((platform) => (
                            <MenuItem key={platform.id} value={platform.id}>
                              <Checkbox checked={values.platformIds.includes(platform.id)} />
                              <ListItemText primary={platform.name} />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText error={Boolean(platformFieldError)}>
                        {helperMessage}
                      </FormHelperText>
                    </FormControl>
                    <TextField
                      name="description"
                      label="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseDialog} disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={isSubmitting || !dirty || !isValid}>
                    Save
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      <Dialog open={Boolean(deleteGroup)} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle>Delete user group</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting {deleteGroup?.name} will remove the group. This action cannot be undone.
          </Typography>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar?.open ?? false}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar && (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default UserGroupsPage;
